import {
  Box,
  Card,
  CardContent,
  Chip,
  Stack,
  Typography,
  alpha,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Business as CompanyIcon,
} from '@mui/icons-material';
import type { CompanyMatch } from '../../types/companyReview';

interface CompanyListProps {
  companies: CompanyMatch[];
  selectedCompany: string | null;
  onSelectCompany: (companyId: string) => void;
}

const getMatchColor = (score: number) => {
  if (score >= 90) return 'success';
  if (score >= 75) return 'warning';
  return 'default';
};

const getMatchBgColor = (score: number) => {
  if (score >= 90) return alpha('#4caf50', 0.06);
  if (score >= 75) return alpha('#ff9800', 0.06);
  return alpha('#9e9e9e', 0.04);
};

const CompanyList: React.FC<CompanyListProps> = ({
  companies,
  selectedCompany,
  onSelectCompany,
}) => {
  return (
    <Stack
      spacing={0.75}
      sx={{
        maxHeight: 180,
        overflowY: 'auto',
        pr: 0.5,
        '&::-webkit-scrollbar': {
          width: 6,
        },
        '&::-webkit-scrollbar-track': {
          bgcolor: 'grey.100',
          borderRadius: 3,
        },
        '&::-webkit-scrollbar-thumb': {
          bgcolor: 'grey.400',
          borderRadius: 3,
          '&:hover': {
            bgcolor: 'grey.500',
          },
        },
      }}>
      {companies.map((company: CompanyMatch) => (
        <Card
          key={company.companyId}
          variant="outlined"
          sx={{
            cursor: 'pointer',
            flexShrink: 0,
            borderColor:
              selectedCompany === company.companyId ? 'primary.main' : 'divider',
            borderWidth: selectedCompany === company.companyId ? 2 : 1,
            bgcolor:
              selectedCompany === company.companyId
                ? alpha('#1976d2', 0.04)
                : getMatchBgColor(company.similarityScore),
            transition: 'all 0.15s ease',
            '&:hover': {
              borderColor: 'primary.light',
              bgcolor: alpha('#1976d2', 0.06),
            },
          }}
          onClick={e => {
            e.stopPropagation();
            onSelectCompany(company.companyId);
          }}>
          <CardContent
            sx={{
              py: 0.75,
              px: 1.5,
              '&:last-child': { pb: 0.75 },
            }}>
            <Stack
              direction="row"
              alignItems="flex-start"
              justifyContent="space-between">
              <Stack
                direction="row"
                alignItems="flex-start"
                spacing={1}
                sx={{ flex: 1, minWidth: 0 }}>
                {selectedCompany === company.companyId ? (
                  <CheckCircleIcon
                    color="primary"
                    sx={{ fontSize: 18, mt: 0.25 }}
                  />
                ) : (
                  <CompanyIcon
                    color="action"
                    sx={{ fontSize: 18, opacity: 0.5, mt: 0.25 }}
                  />
                )}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    fontWeight="medium"
                    fontSize="0.85rem"
                    lineHeight={1.3}
                    sx={{
                      wordBreak: 'break-word',
                      overflowWrap: 'anywhere',
                    }}>
                    {company.companyName}
                  </Typography>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    {company.companyGST && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        fontSize="0.7rem">
                        {company.companyGST}
                      </Typography>
                    )}
                  </Stack>
                </Box>
              </Stack>
              <Chip
                label={`${company.similarityScore}%`}
                size="small"
                color={getMatchColor(company.similarityScore)}
                sx={{
                  fontWeight: 'bold',
                  minWidth: 44,
                  height: 22,
                  fontSize: '0.7rem',
                  ml: 1,
                  flexShrink: 0,
                }}
              />
            </Stack>
          </CardContent>
        </Card>
      ))}
    </Stack>
  );
};

export default CompanyList;
